import { useEffect, useRef, useState } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types";
import type { FailedMeasure, TileDataResponse } from "../../../../../../../../api/types/svTypes.ts";
import { useEnhancedBiApiClientProvider } from "../../../../contexts/ApiClientProviderContext.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import { createCurrentReportAction, createDevToolsAction } from "../../../../store/utils/storeUtils";
import useDeferredBuild from "../../common/hooks/useDeferredBuild";
import useRequestConfigBuilder from "./useRequestConfigBuilder";

type CancellableTileRequest = {
  cancel: () => void;
};

const useDataLoadingBuilder = () => {
  const dispatch = useAppDispatch();
  const { buildTileReport } = useEnhancedBiApiClientProvider();
  const [cancellationToken, setCancellationToken] = useState<CancellableTileRequest | undefined>(undefined);
  const [error, setError] = useState<string>();
  const [isCalculating, setIsCalculating] = useState(false);
  const [failedMeasures, setFailedMeasures] = useState<FailedMeasure[]>([]);
  const [data, setData] = useState<TileDataResponse>();

  const cancellationTokenRef = useRef<CancellableTileRequest | undefined>(cancellationToken);
  useEffect(() => {
    cancellationTokenRef.current = cancellationToken;
  }, [cancellationToken]);

  const { requestConfig } = useRequestConfigBuilder();

  const calculate = (
    onBuildDone: (response: ApiResponse<TileDataResponse>) => void,
    onBuildError: (reason: string | undefined) => void
  ) => {
    setIsCalculating(true);
    setError(undefined);
    dispatch(createCurrentReportAction("setEtag", { etag: undefined, cacheValid: undefined }));

    setCancellationToken(buildTileReport?.(requestConfig, onBuildDone, onBuildError));
  };

  const handleBuildDone = (response: ApiResponse<TileDataResponse>) => {
    if (response.success) {
      if (response.data.errorCode) {
        setError(response.data.errorCode);
      } else {
        setError(undefined);
        setData(response.data);
        dispatch(createDevToolsAction("update", { loggingItems: response.data.loggingItems }));
        dispatch(createCurrentReportAction("setEtag", { etag: response.data.etag, cacheValid: undefined }));
        setFailedMeasures(response.data.failedMeasures);
      }
    } else if (response.error?.message) {
      setError(response.error.message);
    }
    setIsCalculating(false);
  };

  const handleBuildError = (reason: string | undefined) => {
    if (reason === "canceled") {
      setError(undefined);
    } else {
      setError(reason || "An error occurred while calculating report data. Please try again.");
    }
    setIsCalculating(false);
  };

  const { doRequest, resetInitialRequestFlag, cancelCurrentAwaiter } = useDeferredBuild({
    request: calculate,
    handleBuildDone,
    handleBuildError,
    cancelRequest: () => cancellationTokenRef.current?.cancel(),
    onProgressChange: (progress) => dispatch(createCurrentReportAction("updateBuildProgress", progress)),
  });

  const handleInvalidConfiguration = () => {
    cancelCurrentAwaiter();
  };

  useEffect(() => {
    resetInitialRequestFlag();
  }, [requestConfig.reportId, resetInitialRequestFlag]);

  useEffect(() => {
    return () => {
      if (cancellationTokenRef.current !== undefined) {
        cancellationTokenRef.current.cancel();
      }
    };
  }, []);

  return { data, isCalculating, error, calculate: doRequest, failedMeasures, handleInvalidConfiguration };
};

export default useDataLoadingBuilder;
